import React from 'react';

import { useForm } from '@nerdgeschoss/react-use-form-library';
import { Button } from '../../../components/Button/Button';
import { Card } from '../../../components/Card/Card';
import { ContributionListChart } from '../../../components/Charts/contribution_list_chart';
import { FormSelect } from '../../../components/Form/Select/FormSelect';
import { NoData } from '../../../components/NoData/NoData';
import { Stack } from '../../../components/Stack/Stack';
import { Text } from '../../../components/text/text';
import { graphql } from '../../../generated';
import { type StatisticsPointsOfSaleStatistic } from '../../../generated/graphql';
import { t } from '../../../locales/translate';
import { shopPath } from '../../../routes';
import { formatCurrency, formatNumber } from '../../../util/formatters';
import { withSuspendedCard } from '../../../util/suspense';
import { useSuspenseQuery } from '../../../util/use_suspense_query';
import { Spinner } from '../../../components/Spinner/Spinner';

const DASHBOARD_POINTS_OF_SALE = graphql(`
  query PointsOfSale(
    $from: ISO8601DateTime
    $to: ISO8601DateTime
    $statistic: StatisticsPointsOfSaleStatistic
    $first: Int
    $after: String
  ) {
    statistics {
      pointsOfSale(during: { from: $from, to: $to }, statistic: $statistic, first: $first, after: $after) {
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            percentage
            absolute
            shop {
              id
              name
              slug
            }
          }
        }
      }
    }
  }
`);

interface Props {
  from: Date;
  to: Date;
}

function PointsOfSale$({ from, to }: Props): JSX.Element | null {
  const { model, fields } = useForm<{
    statistic: StatisticsPointsOfSaleStatistic;
  }>({
    model: {
      statistic: 'TURNOVER',
    },
  });

  const { data, loadingMore, fetchMore } = useSuspenseQuery(DASHBOARD_POINTS_OF_SALE, {
    variables: {
      from,
      to,
      statistic: model.statistic,
      first: 5,
    },
  });

  const sessionsData = data.statistics?.pointsOfSale.edges.map((edge) => edge.node) || [];
  const chartData =
    sessionsData?.map((item) => ({
      percentage: item.percentage,
      label: item.shop ? item.shop.name : t.component.pointsOfSale.otherShop,
      value: model.statistic !== 'ORDER_COUNT' ? formatCurrency(item.absolute) : formatNumber(item.absolute, 0),
      href: item.shop ? shopPath(item.shop.id) : undefined,
    })) || [];

  const statisticMap: Record<StatisticsPointsOfSaleStatistic, string> = {
    TURNOVER: 'turnover',
    AVERAGE_ORDER_VALUE: 'averageOrderValue',
    ORDER_COUNT: 'orderCount',
  };

  return (
    <Card
      size="dashboard"
      title={<p>{t.component.pointsOfSale.title}</p>}
      actions={
        <FormSelect
          {...fields.statistic}
          fullBorder
          icon="filter"
          options={[
            {
              value: 'TURNOVER',
              label: t.component.pointsOfSale.turnover,
            },
            {
              value: 'AVERAGE_ORDER_VALUE',
              label: t.component.pointsOfSale.averageOrderValue,
            },
            {
              value: 'ORDER_COUNT',
              label: t.component.pointsOfSale.orderCount,
            },
          ]}
          name="statistic"
        />
      }
      footer={
        <Stack fullWidth justify="center">
          {sessionsData?.length && loadingMore ? (
            <Spinner />
          ) : (
            data.statistics?.pointsOfSale.pageInfo.hasNextPage && (
              <Button
                onClick={() => {
                  fetchMore({
                    variables: {
                      after: data?.statistics?.pointsOfSale.pageInfo.endCursor,
                    },
                  });
                }}
                type="secondary-dark"
                title={t.actions.loadMore}
              />
            )
          )}
        </Stack>
      }
    >
      {sessionsData?.length ? (
        <Stack size="md">
          <Text type="dashboard-card-label">{t.component.pointsOfSale[statisticMap[model.statistic]]}</Text>
          <ContributionListChart data={chartData} hidePercentage={model.statistic === 'AVERAGE_ORDER_VALUE'} />
        </Stack>
      ) : (
        <NoData description={t.component.pointsOfSale.noData} />
      )}
    </Card>
  );
}

export const PointsOfSale = withSuspendedCard(PointsOfSale$, {
  loadingTitle: t.component.pointsOfSale.title,
  loadingHeight: 452,
});
